import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useListaPublicacoes } from '@/api/publicacoes';
import { Button } from '@/components/ui/button';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { cn } from '@/lib/utils';
import { formatarData, formatarMesAno } from '@/lib/format';
import {
  barrasDoDia,
  type CalendarioMes,
  deslocamentoSemana,
  isoHoje,
  mesDoIso,
  mesVizinho,
  montarCalendarioMes,
  primeiroDiaIso,
  ultimoDiaIso,
  type BarraCalendario,
  type MesRef,
} from './calendario-geometria';
import { MensagemPublicacoes, SeloSituacao, vigenciaTexto } from './publicacoes-util';

/** Classes completas (o Tailwind so gera o que aparece literal no codigo). Indice = cor da publicacao (1 a 8). */
const COR_FUNDO: Record<number, string> = {
  1: 'bg-cal-1',
  2: 'bg-cal-2',
  3: 'bg-cal-3',
  4: 'bg-cal-4',
  5: 'bg-cal-5',
  6: 'bg-cal-6',
  7: 'bg-cal-7',
  8: 'bg-cal-8',
};
const ALTURA_FAIXA_PX = 36;
const SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const MAX_PONTOS_DIA = 3;

export interface CalendarioPublicacoesProps {
  onAbrir: (id: string) => void;
}

/**
 * Calendario de publicacoes: grade do mes (so no desktop) e linha do tempo com uma barra por publicacao, empilhadas sem
 * sobrepor. Sem arrastar: clicar na barra abre o snapshot. Posicao e largura = dias do mes (geometria, nada de preco).
 */
export function CalendarioPublicacoes({ onAbrir }: CalendarioPublicacoesProps) {
  const [mes, setMes] = useState<MesRef>(() => mesDoIso(isoHoje()));
  const periodo = { de: primeiroDiaIso(mes), ate: ultimoDiaIso(mes) };
  const consulta = useListaPublicacoes(periodo);
  const hoje = isoHoje();
  const hojeNoMes = hoje.slice(0, 7) === periodo.de.slice(0, 7);
  const hojeIndice = Number(hoje.slice(8, 10)) - 1;

  const cal = montarCalendarioMes(consulta.data ?? [], mes);
  const semPublicacoes = consulta.data !== undefined && cal.barras.length === 0;

  return (
    <div className="flex flex-col gap-4 px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-title font-semibold" aria-live="polite">
          {formatarMesAno(mes.ano, mes.mes)}
        </h3>
        <div className="flex items-center gap-1.5">
          <Button type="button" variant="outline" size="icon" aria-label="Mês anterior" onClick={() => setMes((m) => mesVizinho(m, -1))}>
            <ChevronLeft />
          </Button>
          <Button type="button" variant="outline" onClick={() => setMes(mesDoIso(hoje))} disabled={hojeNoMes}>
            Hoje
          </Button>
          <Button type="button" variant="outline" size="icon" aria-label="Próximo mês" onClick={() => setMes((m) => mesVizinho(m, 1))}>
            <ChevronRight />
          </Button>
        </div>
      </div>

      {consulta.isPending && <MensagemPublicacoes papel="status">Carregando calendário…</MensagemPublicacoes>}
      {!consulta.isPending && consulta.isError && (
        <ErroFormacao erro={consulta.error} titulo="Não foi possível carregar o calendário" onTentar={() => void consulta.refetch()} />
      )}

      {!consulta.isPending && !consulta.isError && (
        <>
          <Grade mes={mes} barras={cal.barras} hojeIndice={hojeNoMes ? hojeIndice : null} />
          <LinhaDoTempo cal={cal} hojeIndice={hojeNoMes ? hojeIndice : null} onAbrir={onAbrir} />
          {semPublicacoes && <MensagemPublicacoes>Nenhuma publicação vigente ou programada neste mês.</MensagemPublicacoes>}
          {cal.barras.length > 0 && <Legenda barras={cal.barras} onAbrir={onAbrir} />}
        </>
      )}
    </div>
  );
}

/** Grade do mes em semanas (segunda a domingo). Escondida no celular: la fica so a linha do tempo. */
function Grade({ mes, barras, hojeIndice }: { mes: MesRef; barras: BarraCalendario[]; hojeIndice: number | null }) {
  const dias = new Date(Date.UTC(mes.ano, mes.mes, 0)).getUTCDate();
  const vazios = deslocamentoSemana(mes);
  const celulas: Array<number | null> = [...Array.from({ length: vazios }, () => null), ...Array.from({ length: dias }, (_, i) => i + 1)];
  while (celulas.length % 7 !== 0) celulas.push(null);

  return (
    <div className="hidden md:block" data-testid="grade-calendario">
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-xl border border-border bg-border text-caption">
        {SEMANA.map((s) => (
          <div key={s} className="bg-surface-muted px-2 py-1.5 text-center font-medium text-ink-muted">
            {s}
          </div>
        ))}
        {celulas.map((dia, i) => {
          if (dia === null) return <div key={`v${i}`} className="min-h-16 bg-background" />;
          const indice = dia - 1;
          const doDia = barrasDoDia(barras, indice);
          const ehHoje = hojeIndice === indice;
          return (
            <div
              key={dia}
              data-dia={dia}
              aria-current={ehHoje ? 'date' : undefined}
              aria-label={`${dia}: ${doDia.length === 0 ? 'sem publicação' : `${doDia.length} publicação${doDia.length > 1 ? 'ões' : ''}`}`}
              className={cn('flex min-h-16 flex-col gap-1 bg-card p-1.5', ehHoje && 'ring-2 ring-inset ring-primary')}
            >
              <span className={cn('tabular text-small', ehHoje ? 'font-semibold text-primary' : 'text-ink-body')}>{dia}</span>
              <div className="flex flex-wrap items-center gap-1">
                {doDia.slice(0, MAX_PONTOS_DIA).map((b) => (
                  <span key={b.publicacao.id} aria-hidden="true" className={cn('size-2 rounded-full', COR_FUNDO[b.cor])} />
                ))}
                {doDia.length > MAX_PONTOS_DIA && <span className="text-micro text-ink-muted">+{doDia.length - MAX_PONTOS_DIA}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ALTURA_EIXO_PX = 24;

/** Linha do tempo: um dia por coluna; uma faixa por nivel de empilhamento. Rolagem horizontal no celular. */
function LinhaDoTempo({ cal, hojeIndice, onAbrir }: { cal: CalendarioMes; hojeIndice: number | null; onAbrir: (id: string) => void }) {
  const { dias, barras, faixas } = cal;
  const alturaCorpo = Math.max(faixas, 1) * ALTURA_FAIXA_PX + 8;
  const colunas = { gridTemplateColumns: `repeat(${dias}, minmax(0, 1fr))` };

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <div className="min-w-[640px] px-3 py-2" data-testid="linha-do-tempo">
        <div className="grid text-micro text-ink-muted" style={{ ...colunas, height: ALTURA_EIXO_PX }}>
          {Array.from({ length: dias }, (_, i) => (
            <span key={i} className={cn('tabular text-center leading-6', hojeIndice === i && 'font-semibold text-primary')}>
              {i + 1}
            </span>
          ))}
        </div>
        <div className="relative" style={{ height: alturaCorpo }}>
          {hojeIndice !== null && (
            <div
              data-testid="marca-hoje"
              aria-hidden="true"
              className="absolute inset-y-0 border-x border-primary/40 bg-primary/10"
              style={{ left: `${(hojeIndice / dias) * 100}%`, width: `${(1 / dias) * 100}%` }}
            />
          )}
          {Array.from({ length: dias + 1 }, (_, i) => (
            <div key={i} aria-hidden="true" className="absolute inset-y-0 w-px bg-line-soft" style={{ left: `${(i / dias) * 100}%` }} />
          ))}
          {barras.map((b) => (
            <BarraPublicacao key={b.publicacao.id} barra={b} onAbrir={onAbrir} />
          ))}
        </div>
      </div>
    </div>
  );
}

function BarraPublicacao({ barra: b, onAbrir }: { barra: BarraCalendario; onAbrir: (id: string) => void }) {
  const p = b.publicacao;
  const trecho = `${formatarData(p.validFrom).slice(0, 5)} – ${p.validAte ? formatarData(p.validAte).slice(0, 5) : 'sem fim'}`;
  const texto = `${b.inicioAberto ? '← ' : ''}${trecho}${b.fimAberto ? ' →' : ''}`;
  return (
    <button
      type="button"
      data-testid="barra-publicacao"
      data-cor={b.cor}
      data-inicio={p.validFrom}
      data-fim={p.validAte ?? ''}
      data-faixa={b.faixa}
      data-inicio-aberto={b.inicioAberto ? 'sim' : 'nao'}
      data-fim-aberto={b.fimAberto ? 'sim' : 'nao'}
      onClick={() => onAbrir(p.id)}
      aria-label={`Publicação ${vigenciaTexto(p)}. Abrir snapshot.`}
      title={vigenciaTexto(p)}
      className={cn(
        'absolute flex h-7 items-center overflow-hidden px-2 text-caption font-medium whitespace-nowrap text-white shadow-sm transition-[filter] hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        COR_FUNDO[b.cor],
        b.inicioAberto ? 'rounded-l-none' : 'rounded-l-md',
        b.fimAberto ? 'rounded-r-none' : 'rounded-r-md',
      )}
      style={{ left: `${b.esquerdaPct}%`, width: `${b.larguraPct}%`, top: 4 + b.faixa * ALTURA_FAIXA_PX }}
    >
      <span className="truncate">{texto}</span>
    </button>
  );
}

function Legenda({ barras, onAbrir }: { barras: BarraCalendario[]; onAbrir: (id: string) => void }) {
  return (
    <ul aria-label="Legenda das publicações" className="grid gap-2 text-small sm:grid-cols-2">
      {barras.map((b) => (
        <li key={b.publicacao.id}>
          <button
            type="button"
            onClick={() => onAbrir(b.publicacao.id)}
            className="flex w-full items-start gap-2.5 rounded-lg border border-border bg-card px-3 py-2 text-left hover:bg-accent"
          >
            <span aria-hidden="true" className={cn('mt-1 size-2.5 shrink-0 rounded-[3px]', COR_FUNDO[b.cor])} />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex flex-wrap items-center gap-2">
                <span className="tabular">{vigenciaTexto(b.publicacao)}</span>
                <SeloSituacao situacao={b.publicacao.situacao} />
              </span>
              {b.publicacao.observacao && <span className="truncate text-caption text-ink-muted">{b.publicacao.observacao}</span>}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
